import { useCallback, useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import { toast } from 'sonner';

import { ApiErrorConverter } from '@packages/shared/domain/shared/errors/ApiErrorConverter';
import { API_ASSISTANCE_ERROR_MAP } from '@packages/shared/domain/assistance/errors/AssistanceErrorMap';
import { type UnitResponse } from '@packages/shared/domain/unit/schemas/UnitSchema';

import { Routes } from '@/config/Routes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { PlanLandingResolver } from '@/features/auth/utils/PlanLandingResolver';
import { AssistantHomeHeader } from '@/features/assistant/components/home/AssistantHomeHeader';
import { AssistantHomePage } from '@/features/assistant/components/home/AssistantHomePage';
import { AssistanceSidebar } from '@/features/assistant/components/sidebar/AssistanceSidebar';
import { UnitSelectorModal } from '@/features/assistant/components/UnitSelectorModal';
import { useCreateAssistance } from '@/features/assistant/hooks/useCreateAssistance';
import { useSetHeaderConfig } from '@/features/navigation/hooks/useHeaderConfig';
import { SafeAreaLayout } from '@/features/shared/components/SafeAreaLayout';

export const Route = createFileRoute('/_authenticated/assistances/')({
	component: AssistancesRouteGuard,
});

function AssistancesRouteGuard() {
	const { assistantEnabled, businessEnabled, isLoading } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		if (isLoading) return;
		if (!assistantEnabled) {
			navigate({ to: PlanLandingResolver.resolve({ assistantEnabled, businessEnabled }), replace: true });
		}
	}, [isLoading, assistantEnabled, businessEnabled, navigate]);

	if (isLoading || !assistantEnabled) return null;

	return <AssistancesRoute />;
}

function AssistancesRoute() {
	const [isUnitSelectorOpen, setIsUnitSelectorOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const navigate = useNavigate();
	const { mutate: createAssistance } = useCreateAssistance();
	const { t: tErrors } = useTranslation('errors');

	useSetHeaderConfig({ title: '', displayed: false, fullBleed: true });

	const handleStartAssistance = useCallback(() => {
		posthog.capture('unit_selector_opened');
		setIsUnitSelectorOpen(true);
	}, []);
	const handleCloseUnitSelector = useCallback(() => setIsUnitSelectorOpen(false), []);
	const handleOpenSidebar = useCallback(() => {
		posthog.capture('sidebar_opened');
		setIsSidebarOpen(true);
	}, []);
	const handleCloseSidebar = useCallback(() => setIsSidebarOpen(false), []);

	const handleSidebarNewAssistance = useCallback(() => {
		setIsSidebarOpen(false);
		setIsUnitSelectorOpen(true);
	}, []);

	const handleSidebarSelectAssistance = useCallback(
		(assistanceId: string) => {
			posthog.capture('assistance_resumed', { assistanceId });
			setIsSidebarOpen(false);
			navigate({ to: Routes.ASSISTANCE_CHAT, params: { id: assistanceId } });
		},
		[navigate],
	);

	const handleUnitSelect = useCallback(
		(unit: UnitResponse) => {
			setIsUnitSelectorOpen(false);
			createAssistance(
				{ unitId: unit.id },
				{
					onSuccess: (assistance) => {
						posthog.capture('assistance_created', { unitId: unit.id, unitModel: unit.model });
						navigate({ to: Routes.ASSISTANCE_CHAT, params: { id: assistance.id } });
					},
					onError: (error) => {
						const errorKey = ApiErrorConverter.toTranslationKey(error, API_ASSISTANCE_ERROR_MAP);
						toast.error(tErrors(errorKey));
					},
				},
			);
		},
		[createAssistance, navigate, tErrors],
	);

	return (
		<SafeAreaLayout className="bg-muted">
			<AssistantHomeHeader onMenuPress={handleOpenSidebar} />
			<AssistantHomePage onStartAssistance={handleStartAssistance} />
			<UnitSelectorModal isOpen={isUnitSelectorOpen} onClose={handleCloseUnitSelector} onSelect={handleUnitSelect} />
			<AssistanceSidebar
				isOpen={isSidebarOpen}
				onClose={handleCloseSidebar}
				onNewAssistance={handleSidebarNewAssistance}
				onSelectAssistance={handleSidebarSelectAssistance}
			/>
		</SafeAreaLayout>
	);
}
